import { Select, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Id } from '@feature/utils';

import { getTravelDayBalancePeopleOptions } from '../model/TravelRequestQuery';
import { TravelDayBalancePerson } from '../model/TravelRequestTypes';

const MORE = 'more';

const searchKey = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

interface TravelDayBalancePersonSelectProps {
  year: number;
  facultyId: Id | null;
  selfId: Id;
  selfName: string;
  value: TravelDayBalancePerson | null;
  onChange: (person: TravelDayBalancePerson | null) => void;
  onUnavailable: () => void;
}

export function TravelDayBalancePersonSelect({
  year,
  facultyId,
  selfId,
  selfName,
  value,
  onChange,
  onUnavailable,
}: TravelDayBalancePersonSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selfLabel, setSelfLabel] = useState(selfName);
  // Mantine writes the picked label into the search box, which must not narrow the list.
  const typed = search.trim() === '' || search === (value?.fullName ?? selfLabel) ? null : search.trim();
  const [debouncedSearch] = useDebouncedValue(typed, 400);
  const { data, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery(
    getTravelDayBalancePeopleOptions({ year, facultyId }, debouncedSearch),
  );
  const forbidden = isError && isAxiosError(error) && error.response?.status === 403;
  const selfValue = String(selfId);
  const rows = (data?.pages ?? []).flatMap(page => page.list);
  const selfRow = rows.find(person => String(person.userId) === selfValue);

  useEffect(() => {
    if (forbidden) onUnavailable();
  }, [forbidden, onUnavailable]);
  useEffect(() => {
    if (selfRow != null) setSelfLabel(selfRow.fullName);
  }, [selfRow]);
  if (forbidden) return null;

  // The entries added on top of the loaded pages must obey the search like the rows the backend filtered.
  const matchesSearch = (label: string) => typed == null || searchKey(label).includes(searchKey(typed));
  const people = [...rows];
  // A picked person missing from the loaded pages carries figures from another year or scope.
  const stale = value != null && !rows.some(person => person.userId === value.userId);
  if (stale && matchesSearch(value.fullName)) {
    people.unshift(value);
  }
  const byValue = new Map(people.map(person => [String(person.userId), person]));
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Select
      label={t('travel.requests.dayBalance.person')}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options }) => options}
      data={[
        // The own row may sit on a page not loaded yet, and the default selection must still name the user.
        ...(selfRow == null && matchesSearch(selfLabel) ? [{ value: selfValue, label: selfLabel }] : []),
        ...people.map(person => ({ value: String(person.userId), label: person.fullName })),
        ...(hasNextPage ? [{ value: MORE, label: t('travel.requests.dayBalance.more') }] : []),
      ]}
      value={value == null ? selfValue : String(value.userId)}
      onChange={next => {
        if (next === MORE) {
          loadMore();
        } else if (next === selfValue) {
          onChange(null);
        } else if (next != null) {
          onChange(byValue.get(next) ?? null);
        }
      }}
      allowDeselect={false}
      error={isError ? t('label.serverError') : null}
      nothingFoundMessage={t('travel.requests.dayBalance.nobody')}
      scrollAreaProps={{ onBottomReached: loadMore }}
      renderOption={({ option }) => {
        const person = byValue.get(option.value);
        if (person == null) return option.label;
        return (
          <Stack gap={0}>
            <Text size="sm">{option.label}</Text>
            {person.departmentName != null && (
              <Text
                size="xs"
                c="dimmed"
              >
                {person.departmentName}
              </Text>
            )}
            {(person.vacationDays > 0 || person.unpaidDays > 0) && (
              <Text
                size="xs"
                c="dimmed"
              >
                {t('travel.requests.dayBalance.personLeave', {
                  vacationDays: person.vacationDays,
                  unpaidDays: person.unpaidDays,
                })}
              </Text>
            )}
          </Stack>
        );
      }}
      miw={280}
    />
  );
}
